import { Implement, ImplementArchetype } from "protos/atk/autotractor";
import {
  ImplementBoundaryModel,
  MeasuringTapeImplementBoundaryModelV1,
} from "protos/atk/boundary_model";
import { OptionalViewModel } from "common/utils/viewModels";
import { Polygon2D } from "protos/geometry/geometry";

/**
 * An even more view-model-y ImplementBoundaryModel, with explicit "present" flags
 * for the optional fields so this model can be used with formik.
 */
export type ImplementBoundaryViewModel = Omit<
  ImplementBoundaryModel,
  "measuringTapeV1"
> & {
  measuringTapeV1:
    | (MeasuringTapeImplementBoundaryModelV1 & {
        // TODO: currently, to maintain minimal functional change with before this PR,
        // we treat implement overall width as symmetrical for form input purposes,
        // and lock out editing if it's asymmetrical; this needs to be fixed.
        overallWidthAsymmetrical: boolean;
        overallWidthM: number;
      })
    | undefined;
};

function implementLikeOwnBoundaryViewModelOrDefault(
  implementLike: ImplementArchetype | Implement | undefined
): OptionalViewModel<ImplementBoundaryViewModel> {
  let present: boolean;
  let boundaryModel: ImplementBoundaryModel;

  if (implementLike?.boundaryModel) {
    // already a boundary model, nothing to do
    present = true;
    boundaryModel = implementLike.boundaryModel;
  } else if (implementLike?.boundary) {
    // TEMPORARY (to be removed once everything has a boundary model): probably
    // predated the existence of the boundary model, so infer one from the
    // boundary polygon

    // compute AABB of the boundary polygon
    let minX = 0;
    let maxX = 0;
    let minY = 0;
    let maxY = 0;

    for (const point of implementLike.boundary.points) {
      if (point.x > maxX) {
        maxX = point.x;
      }
      if (point.x < minX) {
        minX = point.x;
      }
      if (point.y > maxY) {
        maxY = point.y;
      }
      if (point.y < minY) {
        minY = point.y;
      }
    }

    present = true;
    boundaryModel = {
      measuringTapeV1: {
        overallLengthM: maxY - minY,
        overallWidthMLeft: -minX,
        overallWidthMRight: maxX,
      },
    };
  } else {
    // neither a boundary model or boundary polygon, probably starting from
    // scratch here
    present = false;
    boundaryModel = {
      measuringTapeV1: {
        overallLengthM: 0,
        overallWidthMLeft: 0,
        overallWidthMRight: 0,
      },
    };
  }

  // lock out editing overall width if it's asymmetrical
  if (boundaryModel.measuringTapeV1) {
    const overallWidthRatio =
      boundaryModel.measuringTapeV1.overallWidthMLeft /
      boundaryModel.measuringTapeV1.overallWidthMRight;
    const overallWidthAsymmetrical =
      overallWidthRatio < 0.98 || overallWidthRatio > 1.02;

    return {
      present,
      value: {
        ...boundaryModel,
        measuringTapeV1: {
          ...boundaryModel.measuringTapeV1,
          overallWidthAsymmetrical,
          overallWidthM:
            boundaryModel.measuringTapeV1.overallWidthMLeft +
            boundaryModel.measuringTapeV1.overallWidthMRight,
        },
      },
    };
  } else {
    return {
      present,
      value: {
        ...boundaryModel,
        measuringTapeV1: undefined,
      },
    };
  }
}

export function implementArchetypeBoundaryViewModel(
  archetype: ImplementArchetype | undefined
): ImplementBoundaryViewModel {
  return implementLikeOwnBoundaryViewModelOrDefault(archetype).value;
}

export function implementBoundaryViewModel(
  implement: Implement | undefined
): OptionalViewModel<ImplementBoundaryViewModel> {
  const implementBoundaryViewModel =
    implementLikeOwnBoundaryViewModelOrDefault(implement);
  const archetypeBoundaryViewModel = implementLikeOwnBoundaryViewModelOrDefault(
    implement?.archetype
  );

  return {
    present: implementBoundaryViewModel.present,
    value: implementBoundaryViewModel.present
      ? implementBoundaryViewModel.value
      : archetypeBoundaryViewModel.value,
  };
}

export function persistImplementBoundaryViewModel(
  viewModel: ImplementBoundaryViewModel,
  existingBoundaryPolygon: Polygon2D | undefined,
  workingWidthMLeft: number,
  workingWidthMRight: number,
  workingStartOffsetM: number
): { boundary: Polygon2D | undefined; boundaryModel: ImplementBoundaryModel } {
  if (viewModel.measuringTapeV1) {
    /*
      generates this sort of shape:
      (so a viewer can see overall vs working widths)
      _ /\ _
      |      |
      |      |
      |______|
     */

    // FIXME once we handle asymmetrical implements properly: convert a modified
    // overall width back to individual half-widths if the old half-widths are
    // symmetrical
    let overallWidthMLeft: number;
    let overallWidthMRight: number;
    if (viewModel.measuringTapeV1.overallWidthAsymmetrical) {
      overallWidthMLeft = viewModel.measuringTapeV1.overallWidthMLeft;
      overallWidthMRight = viewModel.measuringTapeV1.overallWidthMRight;
    } else {
      overallWidthMLeft = viewModel.measuringTapeV1.overallWidthM / 2;
      overallWidthMRight = viewModel.measuringTapeV1.overallWidthM / 2;
    }

    return {
      boundary: {
        points: [
          { x: 0, y: 0 },
          { x: -workingWidthMLeft, y: workingStartOffsetM },
          {
            x: -overallWidthMLeft,
            y: workingStartOffsetM,
          },
          {
            x: -overallWidthMLeft,
            y: viewModel.measuringTapeV1.overallLengthM,
          },
          {
            x: overallWidthMRight,
            y: viewModel.measuringTapeV1.overallLengthM,
          },
          {
            x: overallWidthMRight,
            y: workingStartOffsetM,
          },
          { x: workingWidthMRight, y: workingStartOffsetM },
        ],
      },
      boundaryModel: {
        ...viewModel,
        measuringTapeV1: {
          overallLengthM: viewModel.measuringTapeV1.overallLengthM,
          overallWidthMLeft,
          overallWidthMRight,
        },
      },
    };
  } else {
    return {
      boundary: existingBoundaryPolygon,
      boundaryModel: viewModel,
    };
  }
}
